import {Component, Input} from '@angular/core';
import {NavigationEnd, Router, RouterOutlet} from '@angular/router';
import {ContractorService} from '../../services/contractor.service';
import {BrandingLayoutService} from '../../services/branding-layout.service';
import {NavigationService} from '../../services/ionic/navigation.service';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {AlertService} from '../../services/ionic/alert.service';
import {LoadingService} from '../../services/ionic/loading.service';
import {IonButton, IonCol, IonContent, IonIcon, IonRow, IonText} from '@ionic/angular/standalone';

import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {ResponsiveContainerComponent} from '../../components/responsive-container/responsive-container.component';
import {AnimatedLogoComponent} from '../../components/animated-logo/animated-logo.component';
import {AnimatedContainerComponent} from '../../components/animated-container/animated-container.component';
import {AppInformationComponent} from '../../components/app-information/app-information.component';
import {TypeaheadService} from '../../services/ionic/typeahead.service';
import {AuthService} from '../../services/auth.service';
import {routeUrls} from '../../constants/route-urls';
import {LanguageService} from '../../services/language.service';
import {Contractor} from '../../interfaces/contractor';
import {Theme} from '../../interfaces/theme';
import {Language} from '../../interfaces/language';
import {ThemeService} from '../../services/theme.service';
import {AnimationTrigger} from '../../interfaces/animation-trigger';
import {environment} from '../../../environments/environment';
import {StateDiagramService} from '../../services/state-diagram.service';

@Component({
  selector: 'app-branding-layout',
  templateUrl: './branding-layout.component.html',
  styleUrls: ['./branding-layout.component.scss'],
  imports: [TranslateModule, ResponsiveContainerComponent, AnimatedLogoComponent, AnimatedContainerComponent, AppInformationComponent, IonIcon, IonButton, IonContent, IonText, IonCol, IonRow]
})
export class BrandingLayoutComponent {

  public headerTextAnimationTrigger: AnimationTrigger;

  public headerButtonTextAnimationTrigger: AnimationTrigger;

  public backButtonTextAnimationTrigger: AnimationTrigger;

  public languageButtonTextAnimationTrigger: AnimationTrigger;

  @Input()
  public allowCleanup: boolean;

  @Input()
  public routerOutlet?: RouterOutlet;

  public get headerText(): string {
    if (this._headerText !== this.brandingLayoutService.headerText) {
      this._headerText = null;
      this.headerTextAnimationTrigger.animationTrigger = this._headerText;

      setTimeout((): void => {
        this._headerText = this.brandingLayoutService.headerText;
        this.headerTextAnimationTrigger.animationTrigger = this._headerText;
      });
    }

    return this._headerText;
  }

  private _headerText: string;

  public get headerButtonText(): string {
    let headerButtonText: string = null;

    if (this.brandingLayoutService.hideHeaderButton !== true) {
      if (this.authService.isLoggedIn) {
        headerButtonText = this.authService.fullName;
      } else if (this.contractorService.hasBackend) {
        headerButtonText = this.contractorService.contractorName;
      }
    }

    if (this._headerButtonText !== headerButtonText) {
      this._headerButtonText = null;
      this.headerButtonTextAnimationTrigger.animationTrigger = this._headerButtonText;

      setTimeout((): void => {
        this._headerButtonText = headerButtonText;
        this.headerButtonTextAnimationTrigger.animationTrigger = this._headerButtonText;
      });
    }

    return this._headerButtonText;
  }

  private _headerButtonText: string;

  private _backButtonText: string

  public get backButtonText(): string {
    let backButtonText: string = null;

    if (this.brandingLayoutService.backButton) {
      backButtonText = this.translateService.instant('Back');
    }

    if (this._backButtonText !== backButtonText) {
      this._backButtonText = null;
      this.backButtonTextAnimationTrigger.animationTrigger = this._backButtonText;

      setTimeout((): void => {
        this._backButtonText = backButtonText;
        this.backButtonTextAnimationTrigger.animationTrigger = this.backButtonText;
      });
    }

    return this._backButtonText;
  }

  private _languageButtonText: string;

  public get languageButtonText(): string {
    let languageButtonText: string = null;

    if (this.languageService?.selectedLanguage?.name) {
      languageButtonText = this.languageService?.selectedLanguage?.name;
    }

    if (this._languageButtonText !== languageButtonText) {
      this._languageButtonText = null;
      this.languageButtonTextAnimationTrigger.animationTrigger = this._languageButtonText;

      setTimeout((): void => {
        this._languageButtonText = languageButtonText;
        this.languageButtonTextAnimationTrigger.animationTrigger = this._languageButtonText;
      });
    }

    return this._languageButtonText;
  }

  public constructor(public brandingLayoutService: BrandingLayoutService,
                     public languageService: LanguageService,
                     private contractorService: ContractorService,
                     private authService: AuthService,
                     private alertService: AlertService,
                     private loadingService: LoadingService,
                     private navigationService: NavigationService,
                     private typeaheadService: TypeaheadService,
                     private translateService: TranslateService,
                     private themeService: ThemeService,
                     private stateDiagramService: StateDiagramService,
                     router: Router) {
    this.headerTextAnimationTrigger = {};
    this.headerButtonTextAnimationTrigger = {};
    this.backButtonTextAnimationTrigger = {};
    this.languageButtonTextAnimationTrigger = {};

    router.events.pipe(takeUntilDestroyed()).subscribe((event): void => {
      if (event instanceof NavigationEnd) {
        brandingLayoutService.reset();

        // changeDetectorRef.detectChanges();
      }
    });
  }

  public async headerButtonClick(): Promise<void> {
    if (this.authService.isLoggedIn) {
      await this.switchUser();
    } else if (this.contractorService.hasBackend) {
      await this.selectContractor();
    }
  }

  public async selectContractor(): Promise<void> {
    const availableContractors: Contractor[] = this.contractorService.getAvailableContractors();

    await this.typeaheadService.presentTypeahead({
      title: 'Contractors',
      addItemLabel: 'Add New Contractor',
      allowEmptySelection: false,
      items: availableContractors,
      labelHandler: (contractor: Contractor): string => {
        return contractor?.name;
      },
      selection: availableContractors?.find((availableContractor: Contractor): boolean => {
        return availableContractor?.id === this.contractorService.selectedContractorCode;
      }),
      emptyMessage: 'No contractors found.',
      trackHandler: (index: number, contractor: Contractor): string => {
        return contractor?.id;
      },
      addHandler: async (): Promise<void> => {
        await this.navigationService.navigateBack(routeUrls.addContractor);
      },
      doneHandler: async (contractor: Contractor): Promise<void> => {
        await this.loadingService.presentLoading();

        try {
          this.contractorService.activateContractor(contractor);
          await this.contractorService.selectContractor(contractor);

          const preferredThemeId: string = this.themeService.preferredThemeId;
          const theme: Theme = this.themeService.getPreferredTheme(preferredThemeId);
          this.themeService.activateTheme(theme);

          const preferredLocale: string = this.languageService.preferredLocale;
          const language: Language = this.languageService.getPreferredLanguage(preferredLocale);
          this.languageService.activateLanguage(language);

        } finally {
          await this.loadingService.dismissLoading();
        }
      }
    });

    // if (this.currentUrl === '/' + routeUrls.addContractor) {
    //   // await this.navigationService.navigateRootForward(routeUrls.login);
    // } else {
    //   // await this.navigationService.navigateBack(routeUrls.addContractor);
    // }
    // await this.loadingService.presentLoading();
    //
    // const contractors: Contractor[] = await this.contractorService.getAvailableContractors();
    // const selectedContractorName: string = await this.contractorService.getContractorName();
    //
    // await this.loadingService.dismissLoading();
    //
    // await this.typeaheadService.presentTypeahead({
    //   title: 'Contractors',
    //   items: contractors,
    //   selection: contractors.find((contractor: Contractor): boolean => {
    //     return contractor && selectedContractorName === contractor.name;
    //   }),
    //   multiple: false,
    //
    //
    //   emptyMessage: 'No contractors found.',
    //
    //   labelHandler: (contractor: Contractor): string => {
    //     return contractor.name;
    //   }
    //
    //
    //
    //   // noItemsMessage: 'No contractors found.',
    //   // addLabel: 'Add New Contractor',
    //   // removeHeader: 'Remove Contractor',
    //   //
    //   // items: [],
    //   // selection: [],
    //
    //   // labelHandler: (contractor: Contractor): string => {
    //   //   return contractor.name;
    //   // },
    //   // done: async (contractor: Contractor): Promise<void> => {
    //   //   this.contractorService.selectContractor(contractor);
    //   //
    //   //   if (this.currentUrl === '/' + RouteUrl.addContractor) {
    //   //     await this.navigationService.navigateRootForward(RouteUrl.login);
    //   //   }
    //   // },
    //   // add: async (): Promise<void> => {
    //   //   await this.navigationService.navigateBack(RouteUrl.addContractor);
    //   // },
    //   // remove: async (contractor: Contractor): Promise<void> => {
    //   //   this.contractorService.removeContractor(contractor);
    //   // }
    // });
  }

  public async switchUser(): Promise<void> {
    await this.alertService.presentAlert({
      header: 'Switch User',
      message: 'To switch users, you must first logout the currently logged in user. Are you sure you want this?',
      buttons: [
        'Cancel',
        {
          text: 'Logout',
          role: 'destructive',
          handler: () => {
            this.loadingService.runWithLoading(async () => {
              await this.authService.logout();
              await this.navigationService.navigateRootBack(routeUrls.login);
            });
          },
        },
      ],
    });
  }

  public async changeLanguage(): Promise<void> {
    await this.languageService.showLanguageSelection();
  }

  public openSupportUrl() {
    window.open(environment.teamViewerUrl, '_blank');
  }

}
